/**
 * AccountMenu — dropdown for account actions (settings, manage account, sign out).
 *
 * Placed on the right side of the nav bar (separate from ViewSwitcher).
 * Shows the current user's emoji/avatar as the trigger.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClerk } from '@clerk/react';
import { useCurrentUser } from '../hooks/useCurrentUser.jsx';

function ChevronDown() {
    return (
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
    );
}

/**
 * @param {{ settingsLinks?: Array<{ path: string, label: string, icon?: React.ReactNode, badge?: number }>, unreadCount?: number }} props
 */
export default function AccountMenu({ settingsLinks = [], unreadCount = 0 }) {
    const { openUserProfile, signOut } = useClerk();
    const navigate = useNavigate();
    const currentUser = useCurrentUser();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    // Close on click outside
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open]);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={[
                    'relative flex items-center gap-1.5 h-9 px-2.5 rounded-full',
                    'bg-surface border border-border-subtle',
                    'text-sm text-text-primary',
                    'hover:border-border-strong transition-colors duration-150',
                    open ? 'border-accent-primary ring-1 ring-accent-primary/30' : '',
                ].join(' ')}
            >
                {currentUser?.emoji ? (
                    <span className="text-base leading-none">{currentUser.emoji}</span>
                ) : (
                    <svg className="h-4 w-4 text-text-secondary" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                    </svg>
                )}
                <ChevronDown />
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-bg-primary" />
                )}
            </button>

            {open && (
                <div className="absolute right-0 mt-1 w-44 rounded-lg bg-surface border border-border-subtle shadow-lg shadow-black/30 py-1 z-50">
                    {settingsLinks.map((link) => (
                        <button
                            key={link.path}
                            type="button"
                            onClick={() => { navigate(link.path); setOpen(false); }}
                            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-secondary transition-colors duration-100"
                        >
                            {link.icon && <span className="h-4 w-4 flex items-center justify-center">{link.icon}</span>}
                            <span className="flex-1 text-left">{link.label}</span>
                            {link.badge > 0 && (
                                <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
                                    {link.badge > 99 ? '99+' : link.badge}
                                </span>
                            )}
                        </button>
                    ))}
                    {settingsLinks.length > 0 && (
                        <div className="my-1 border-t border-border-subtle" />
                    )}
                    <button
                        type="button"
                        onClick={() => { openUserProfile(); setOpen(false); }}
                        className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-secondary transition-colors duration-100"
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                        </svg>
                        <span>Manage Account</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => { signOut(); setOpen(false); }}
                        className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-bg-secondary transition-colors duration-100"
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
                        </svg>
                        <span>Sign Out</span>
                    </button>
                </div>
            )}
        </div>
    );
}
